import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const IntroAnimation = ({ onComplete }) => {
  const [isComplete, setIsComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    // Check if intro has been shown in this session
    const introShown = sessionStorage.getItem('oda_intro_shown');
    if (introShown) {
      onComplete();
      return;
    }

    // Start logo animation immediately
    setHasStarted(true);

    // Auto-complete on mobile after 1.5 seconds
    const isMobile = window.innerWidth < 768;
    let autoCompleteTimer;
    
    if (isMobile) {
      autoCompleteTimer = setTimeout(() => {
        handleComplete();
      }, 1500);
    }

    // Desktop: Complete on any mouse movement
    const handleMouseMove = () => {
      if (!isMobile && hasStarted) {
        handleComplete();
      }
    };

    // Desktop: Complete on any click
    const handleClick = () => {
      if (hasStarted) {
        handleComplete();
      }
    };

    if (!isMobile) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('click', handleClick);
    }

    return () => {
      if (autoCompleteTimer) clearTimeout(autoCompleteTimer);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStarted]);

  const handleComplete = () => {
    if (isComplete) return;
    setIsComplete(true);
    sessionStorage.setItem('oda_intro_shown', 'true');
    
    // Delay callback to allow exit animation to complete
    setTimeout(() => {
      onComplete();
    }, 800);
  };

  return (
    <AnimatePresence>
      {!isComplete && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            y: -100,
            filter: 'blur(20px)'
          }}
          transition={{ 
            duration: 0.8,
            ease: [0.43, 0.13, 0.23, 0.96]
          }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-[#0a1628] via-[#071B34] to-[#0d1f3a] overflow-hidden"
        >
          {/* Subtle animated background particles */}
          <div className="absolute inset-0 opacity-30">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-cyan-400 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  opacity: [0.2, 0.8, 0.2],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>

          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Logo container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
              }}
              transition={{ 
                duration: 0.8,
                ease: [0.43, 0.13, 0.23, 0.96]
              }}
              className="relative"
            >
              {/* Glow effect behind logo */}
              <motion.div
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 blur-3xl bg-gradient-to-r from-cyan-500 via-teal-500 to-cyan-500 opacity-40"
                style={{ transform: 'scale(1.5)' }}
              />

              {/* Logo */}
              <motion.img
                src="https://customer-assets.emergentagent.com/job_oda-courses/artifacts/q8rv8hyg_ODA%20LOGO%20-%20Copy.jpeg"
                alt="Overseas Dental Achievers"
                className="relative h-32 md:h-40 w-auto object-contain"
                animate={{
                  filter: [
                    'drop-shadow(0 0 20px rgba(0, 183, 178, 0.3))',
                    'drop-shadow(0 0 40px rgba(0, 183, 178, 0.5))',
                    'drop-shadow(0 0 20px rgba(0, 183, 178, 0.3))',
                  ]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>

            {/* Academy name */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-8 text-center"
            >
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-wide">
                OVERSEAS DENTAL ACHIEVERS
              </h1>
              <p className="text-sm md:text-base text-cyan-300 mt-2 font-light tracking-widest">
                INTERNATIONAL DENTAL EXCELLENCE
              </p>
            </motion.div>

            {/* Red accent line */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 200, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
              className="mt-6 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent"
            />

            {/* Hint text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.6, 0.6] }}
              transition={{ delay: 1.2, duration: 1.5, repeat: Infinity }}
              className="mt-12 text-sm text-gray-400 hidden md:block"
            >
              Move your cursor to continue
            </motion.p>
          </div>

          {/* Corner accent lines */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ delay: 0.6 }}
          >
            {/* Top left */}
            <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-cyan-400/30" />
            {/* Top right */}
            <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-cyan-400/30" />
            {/* Bottom left */}
            <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-cyan-400/30" />
            {/* Bottom right */}
            <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-cyan-400/30" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroAnimation;
